import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon,
  MagnifyingGlassIcon, FunnelIcon, XMarkIcon, CalendarIcon
} from '@heroicons/react/24/outline';

const Devices = ({ status }) => {
  const [devices, setDevices] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    size: 10
  });
  
  // Filter states - matches API: /api/devices/filter
  const [filters, setFilters] = useState({
    deviceTypeId: '',
    orientationTypeId: '',
    startedFrom: '', // Single datetime parameter, not a range
    active: status === 'active' ? true : status === 'inactive' ? false : null
  });
  
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [orientationTypes, setOrientationTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { loading, get, del } = useApi();
  const navigate = useNavigate();

  // Fetch filter options on mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch devices when filters or page changes
  useEffect(() => {
    fetchDevices();
  }, [filters, pageInfo.currentPage, searchTerm]);

  const fetchFilterOptions = async () => {
    // Fetch device types for filter dropdown
    const deviceTypesRes = await get('/api/device-types');
    if (deviceTypesRes.data) setDeviceTypes(deviceTypesRes.data);

    // Fetch orientation types for filter dropdown
    const orientationTypesRes = await get('/api/orientation-types');
    if (orientationTypesRes.data) setOrientationTypes(orientationTypesRes.data);
  };

  const fetchDevices = async () => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add pagination
    params.append('page', pageInfo.currentPage);
    params.append('size', pageInfo.size);
    
    // Add filters - only what API accepts
    if (filters.deviceTypeId) params.append('deviceTypeId', filters.deviceTypeId);
    if (filters.orientationTypeId) params.append('orientationTypeId', filters.orientationTypeId);
    if (filters.startedFrom) params.append('startedFrom', filters.startedFrom); // Single datetime
    if (filters.active !== null) params.append('active', filters.active);
    
    const endpoint = `/api/devices/filter?${params.toString()}`;
    
    const { data } = await get(endpoint);
    if (data) {
      let filteredData = data.content || [];
      
      // Client-side search if searchTerm provided (since API doesn't support text search)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredData = filteredData.filter(device => 
          device.brand?.toLowerCase().includes(term) ||
          device.devType?.toLowerCase().includes(term) ||
          device.other?.toLowerCase().includes(term) ||
          device.deviceType?.description?.toLowerCase().includes(term) ||
          device.orientationType?.description?.toLowerCase().includes(term)
        );
      }
      
      setDevices(filteredData);
      setPageInfo({
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        currentPage: data.number || 0,
        size: data.size || 10
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this device?')) {
      const { error } = await del(`/api/devices/${id}`);
      if (!error) {
        fetchDevices();
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset to first page when filter changes
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDevices();
  };

  const clearFilters = () => {
    setFilters({
      deviceTypeId: '',
      orientationTypeId: '',
      startedFrom: '',
      active: status === 'active' ? true : status === 'inactive' ? false : null
    });
    setSearchTerm('');
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const columns = [
    { header: 'ID', accessor: 'id' },
    { header: 'Brand', accessor: 'brand' },
    { header: 'Device Type', accessor: (row) => row.deviceType?.description || '-' },
    { header: 'Dev Type', accessor: 'devType' },
    { header: 'Orientation', accessor: (row) => row.orientationType?.description || '-' },
    { header: 'Other', accessor: 'other' },
    { header: 'Started At', accessor: (row) => row.startedAt ? new Date(row.startedAt).toLocaleDateString() : '-' },
    { 
      header: 'Status', 
      accessor: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      header: 'Actions', 
      accessor: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/devices/${row.id}`)}
            className="text-blue-600 hover:text-blue-900"
            title="View"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => navigate(`/devices/edit/${row.id}`)}
            className="text-green-600 hover:text-green-900"
            title="Edit"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="text-red-600 hover:text-red-900"
            title={row.isActive ? 'Deactivate' : 'Delete'}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Devices {status && `- ${status.charAt(0).toUpperCase() + status.slice(1)}`}
        </h1>
        <button
          onClick={() => navigate('/devices/add')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Device</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by brand, type, or other..."
              className="input-field pl-10"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Search
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${
              showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {(filters.deviceTypeId || filters.orientationTypeId || filters.startedFrom || searchTerm) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <XMarkIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </form>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <FunnelIcon className="h-4 w-4 mr-1" />
            Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Device Type Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Device Type
              </label>
              <select
                name="deviceTypeId"
                value={filters.deviceTypeId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Device Types</option>
                {deviceTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Orientation Type Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Orientation Type
              </label>
              <select
                name="orientationTypeId"
                value={filters.orientationTypeId}
                onChange={handleFilterChange}
                className="input-field text-sm"
              >
                <option value="">All Orientation Types</option>
                {orientationTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Started From Date Filter - Single datetime */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" />
                Started From
              </label>
              <input
                type="datetime-local"
                name="startedFrom"
                value={filters.startedFrom || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Show devices started after this date</p>
            </div>
          </div>

          {/* Quick date filters - Only for startedFrom */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2 self-center">Quick dates:</span>
            <button
              onClick={() => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                setFilters(prev => ({ ...prev, startedFrom: yesterday.toISOString().slice(0, 16) }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 24 Hours
            </button>
            <button
              onClick={() => {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                setFilters(prev => ({ ...prev, startedFrom: weekAgo.toISOString().slice(0, 16) }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => {
                const monthAgo = new Date();
                monthAgo.setMonth(monthAgo.getMonth() - 1);
                setFilters(prev => ({ ...prev, startedFrom: monthAgo.toISOString().slice(0, 16) }));
              }}
              className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-full hover:bg-purple-200"
            >
              Last 30 Days
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, startedFrom: '' }))}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-800 rounded-full hover:bg-gray-200"
            >
              Clear Date
            </button>
          </div>

          {/* Quick status filters */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2 self-center">Quick status:</span>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: true }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.active === true 
                  ? 'bg-green-500 text-white' 
                  : 'bg-green-100 text-green-800 hover:bg-green-200'
              }`}
            >
              Active Only
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: false }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.active === false 
                  ? 'bg-red-500 text-white' 
                  : 'bg-red-100 text-red-800 hover:bg-red-200'
              }`}
            >
              Inactive Only
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: null }))}
              className={`px-3 py-1 text-xs rounded-full hover:bg-opacity-80 ${
                filters.active === null 
                  ? 'bg-gray-500 text-white' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              All Devices
            </button>
          </div>
        </div>
      )}

      {/* Results count with status indicator */}
      <div className="mb-4 text-sm text-gray-600 flex justify-between items-center">
        <span>Showing {devices.length} of {pageInfo.totalElements} devices</span>
        {filters.active === true && (
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
            Active Only
          </span>
        )}
        {filters.active === false && (
          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
            Inactive Only
          </span>
        )}
        {filters.startedFrom && (
          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
            From: {new Date(filters.startedFrom).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Data Table */}
      <DataTable 
        columns={columns} 
        data={devices}
        pageInfo={pageInfo}
        onPageChange={(page) => {
          setPageInfo({...pageInfo, currentPage: page});
        }}
      />
    </div>
  );
};

export default Devices;